import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, MapPin, Check } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const PermissionRequestModal = () => {
    const { user } = useAuthStore();
    const [isOpen, setIsOpen] = useState(false);
    const [notifStatus, setNotifStatus] = useState<NotificationPermission>('default');
    const [geoStatus, setGeoStatus] = useState<'granted' | 'denied' | 'prompt'>('prompt');
    const [isRequesting, setIsRequesting] = useState(false);

    // Use the push notification hook for proper subscription handling
    const { enable: enablePushNotifications, permission: hookPermission } = usePushNotifications();

    // Check status on mount
    useEffect(() => {
        if (!user) return;

        // Check Notifications
        if ('Notification' in window) {
            setNotifStatus(Notification.permission);
        }

        // Check Geolocation (approximate via Permissions API if available)
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' }).then(result => {
                setGeoStatus(result.state);
            });
        }

        // Show if either is not granted (and not permanently denied if we can help it, but usually we just ask)
        // We use a flag in localStorage to avoid nagging every single refresh if they explicitly skipped
        const hasSkipped = localStorage.getItem('permissions_skipped_v1');

        if ('Notification' in window && Notification.permission === 'default' && !hasSkipped) {
            setIsOpen(true);
        }
    }, [user]);

    // Sync with hook's permission state
    useEffect(() => {
        if (hookPermission) {
            setNotifStatus(hookPermission);
        }
    }, [hookPermission]);

    const requestPermissions = async () => {
        setIsRequesting(true);
        let nStatus = notifStatus;
        let successCount = 0;

        // 1. Request Notification and Enable Push Subscription
        if ('Notification' in window && notifStatus === 'default') {
            try {
                console.log('📱 Requesting notification permission...');

                // Use the hook's enable function which:
                // - Requests permission
                // - Subscribes to push
                // - Saves to backend
                // - Updates the settings toggle
                const success = await enablePushNotifications();

                if (success) {
                    setNotifStatus('granted');
                    nStatus = 'granted';
                    successCount++;
                    console.log('✅ Notifications enabled successfully');
                } else {
                    console.log('⚠️ Notification permission not granted');
                }
            } catch (e) {
                console.error('❌ Error enabling notifications:', e);
                toast.error('Failed to enable notifications');
            }
        } else if (notifStatus === 'granted') {
            // Already granted, just ensure subscription is active
            try {
                await enablePushNotifications();
                successCount++;
            } catch (e) {
                console.error('❌ Error activating notification subscription:', e);
            }
        }

        // 2. Request Geolocation
        if ('geolocation' in navigator && geoStatus !== 'granted') {
            navigator.geolocation.getCurrentPosition(
                () => {
                    setGeoStatus('granted');
                    successCount++;
                    toast.success("📍 Location access granted!");
                },
                (err) => {
                    console.error('❌ Geolocation error:', err);
                    setGeoStatus('denied');
                    toast.error("Location access denied.");
                }
            );
        } else if (geoStatus === 'granted') {
            successCount++;
        }

        // Close after a short delay
        setTimeout(() => {
            setIsRequesting(false);
            setIsOpen(false);

            if (successCount > 0) {
                toast.success(`✅ ${successCount} permission${successCount > 1 ? 's' : ''} granted!`);
            } else {
                toast.info("Permissions were not granted. You can enable them later in settings.");
            }
        }, 1500);
    };

    const handleSkip = () => {
        localStorage.setItem('permissions_skipped_v1', 'true');
        setIsOpen(false);
        toast.info("You can enable permissions anytime from your settings.");
    };

    if (!user) return null;

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Enable Full Experience</DialogTitle>
                    <DialogDescription>
                        To track shipments and receive updates, please enable permissions.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                        <div className={`p-2 rounded-full ${notifStatus === 'granted' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                            {notifStatus === 'granted' ? <Check className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                        </div>
                        <div className="flex-1">
                            <p className="font-medium">Push Notifications</p>
                            <p className="text-xs text-muted-foreground">Get updates on job status & offers.</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                        <div className={`p-2 rounded-full ${geoStatus === 'granted' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                            {geoStatus === 'granted' ? <Check className="w-5 h-5" /> : <MapPin className="w-5 h-5" />}
                        </div>
                        <div className="flex-1">
                            <p className="font-medium">Location Access</p>
                            <p className="text-xs text-muted-foreground">Required for live tracking & routing.</p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button variant="ghost" onClick={handleSkip} disabled={isRequesting}>
                        Skip for now
                    </Button>
                    <Button
                        onClick={requestPermissions}
                        className="gap-2"
                        disabled={isRequesting}
                    >
                        {isRequesting ? 'Enabling...' : 'Enable All Permissions'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PermissionRequestModal;
